import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PayslipService, EmailData } from '@/services/payslipService';
import { toast } from 'sonner';

interface EmailPayslipDialogProps {
  isOpen: boolean;
  onClose: () => void;
  payslipId: string;
  employeeName?: string;
  period?: string;
  defaultEmail?: string;
}

export const EmailPayslipDialog = ({ 
  isOpen, 
  onClose, 
  payslipId, 
  employeeName, 
  period,
  defaultEmail
}: EmailPayslipDialogProps) => {
  const [emailData, setEmailData] = useState<EmailData>({
    email: defaultEmail || '',
    subject: `Payslip for ${period || 'this period'}`,
    message: `Dear ${employeeName || 'Employee'},\n\nPlease find attached your payslip for ${period || 'this period'}.\n\nBest regards,\nFinance Department`
  });
  const [sending, setSending] = useState(false);

  // Keep form fields in sync when dialog opens or when defaults change
  useEffect(() => {
    if (!isOpen) return;
    setEmailData({
      email: defaultEmail || '',
      subject: `Payslip for ${period || 'this period'}`,
      message: `Dear ${employeeName || 'Employee'},\n\nPlease find attached your payslip for ${period || 'this period'}.\n\nBest regards,\nFinance Department`
    });
  }, [isOpen, defaultEmail, employeeName, period]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailData.email) {
      toast.error('Please enter an email address');
      return;
    }

    if (!emailData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSending(true);
    try {
      await PayslipService.emailPayslip(payslipId, emailData);
      onClose();
      setEmailData({
        email: defaultEmail || '',
        subject: `Payslip for ${period || 'this period'}`,
        message: `Dear ${employeeName || 'Employee'},\n\nPlease find attached your payslip for ${period || 'this period'}.\n\nBest regards,\nFinance Department`
      });
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setSending(false);
    }
  };

  const handleChange = (field: keyof EmailData, value: string) => {
    setEmailData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Payslip via Email</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={emailData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="employee@company.com"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={emailData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder="Payslip for this period"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={emailData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Enter your message here..."
              rows={4}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={sending}>
              {sending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 